// Kinio 錯誤：HTTP 4xx/5xx + `{"error":"CODE","message":"…","detail"?:{…}}`（SPEC #eng-10）。
// 機器可讀的補充欄位（mfa_token、命令物件、欄位名、重試秒數…）一律在 `detail` 物件；頂層只有 error / message / detail。

export class KinioError extends Error {
  readonly status: number;
  readonly code: string;
  /** 錯誤 body 的 `detail` 物件（例如 403 ACCOUNT_DELETION_PENDING 的 cancel_token）；沒有 → {}。 */
  readonly detail: Record<string, unknown>;
  /** 限流要等幾秒（429 body `detail.retry_after_seconds`，或 `Retry-After` 標頭）；沒有 → undefined。 */
  readonly retryAfterS?: number;
  /** 錯誤回應的整個 JSON body（409 PTZ lease 的 holder_role / can_force 這類頂層欄位）；非 JSON / 自己造的錯誤 → undefined。 */
  readonly body?: Record<string, unknown>;

  constructor(status: number, code: string, message?: string, detail: Record<string, unknown> = {}, retryAfterS?: number, body?: Record<string, unknown>) {
    super(message || code);
    this.name = 'KinioError';
    this.status = status;
    this.code = code;
    this.detail = detail;
    this.retryAfterS = retryAfterS;
    this.body = body;
  }

  /** 網路層失敗（fetch 拋例外，沒有 HTTP 狀態）。 */
  static network(cause: unknown): KinioError {
    const msg = cause instanceof Error ? cause.message : String(cause);
    return new KinioError(0, 'NETWORK', msg);
  }
}

export function isKinioError(e: unknown): e is KinioError {
  return e instanceof KinioError;
}

/**
 * 是不是「太頻繁，等一下再試」的限流（`RATE_LIMITED` 或任何 429）。
 * 匯出的「每 24 小時一次」（`EXPORT_RATE_LIMITED`）是業務規則，不算。
 */
export function isRateLimited(e: unknown): boolean {
  if (!(e instanceof KinioError)) return false;
  if (e.code === 'EXPORT_RATE_LIMITED') return false;
  return e.code === 'RATE_LIMITED' || e.status === 429;
}

/**
 * 驗證碼每日上限（429 MFA_DAILY_LIMIT，identity 66f94e7：每人每個 UTC 日錯 30 次 → 鎖到下一個 UTC 日 00:00）。
 * 回解鎖時間：`detail.locked_until`（RFC 3339）優先，否則 now + detail.retry_after_seconds；不是這個錯誤 → null。
 */
export function mfaDailyLockOf(e: unknown, now: number = Date.now()): Date | null {
  if (!(e instanceof KinioError) || e.code !== 'MFA_DAILY_LIMIT') return null;
  const lu = typeof e.detail.locked_until === 'string' ? Date.parse(e.detail.locked_until) : NaN;
  if (Number.isFinite(lu)) return new Date(lu);
  return new Date(now + (e.retryAfterS && e.retryAfterS > 0 ? e.retryAfterS : 86_400) * 1000);
}

/** `Retry-After`：秒數或 HTTP 日期 → 秒（無條件進位）；無法解析 → undefined。 */
export function parseRetryAfter(v: string | null | undefined, now: number = Date.now()): number | undefined {
  if (!v) return undefined;
  const t = v.trim();
  if (/^\d+$/.test(t)) return Number(t);
  const at = Date.parse(t);
  return Number.isFinite(at) ? Math.max(0, Math.ceil((at - now) / 1000)) : undefined;
}

/** 從回應 body 解析錯誤碼（body 可能不是 JSON）。 */
export async function errorFromResponse(res: Response): Promise<KinioError> {
  let code = `HTTP_${res.status}`;
  let message: string | undefined;
  let detail: Record<string, unknown> = {};
  let raw: Record<string, unknown> | undefined;
  try {
    const body = (await res.json()) as { error?: unknown; message?: unknown; detail?: unknown };
    if (body && typeof body === 'object' && !Array.isArray(body)) {
      raw = body as Record<string, unknown>;
      if (typeof body.error === 'string' && body.error) code = body.error;
      if (typeof body.message === 'string' && body.message) message = body.message;
      if (body.detail && typeof body.detail === 'object' && !Array.isArray(body.detail)) detail = body.detail as Record<string, unknown>;
    }
  } catch {
    /* non-JSON body */
  }
  // 跨源時 Retry-After 標頭要伺服端 Access-Control-Expose-Headers 才讀得到 → body 的 detail.retry_after_seconds 優先。
  const secs = detail.retry_after_seconds;
  const bodySecs = typeof secs === 'number' && Number.isFinite(secs) ? Math.max(0, Math.ceil(secs)) : undefined;
  const retryAfterS = bodySecs ?? parseRetryAfter(res.headers?.get('Retry-After'));
  return new KinioError(res.status, code, message ?? `${code} (${res.status})`, detail, retryAfterS, raw);
}
